import { Building2 } from 'lucide-react';
import { Button, Card, CardBody, Skeleton } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { useEmployeesByDepartment } from '@/api/employees';
import { departmentColumns, type DepartmentRow } from './employee-list-columns';

/**
 * Departments tab — groups employees by department from
 * `GET /employees/departments`. The endpoint returns null when no department
 * data is available; falls back gracefully to an empty state.
 */
export function EmployeeDepartmentsSection() {
  const { data, isLoading, isError, refetch } = useEmployeesByDepartment();

  const rows: DepartmentRow[] = (data ?? []).map((d) => ({
    department_id: d.department_id,
    department_name: d.department_name,
    department_code: d.department_code,
    department_status: d.department_status,
    department_leader_name: d.department_leader_name,
    employee_count: Array.isArray(d.employees) ? d.employees.length : undefined,
  }));

  if (isError) {
    return (
      <Alert
        tone="danger"
        title="Failed to load departments"
        actions={
          <Button size="sm" variant="ghost" onClick={() => refetch()}>
            Retry
          </Button>
        }
      />
    );
  }

  if (isLoading) {
    return (
      <Card flat>
        <CardBody style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} style={{ height: 24 }} />
          ))}
        </CardBody>
      </Card>
    );
  }

  return (
    <DataTable
      columns={departmentColumns}
      data={rows}
      getRowId={(row) => String(row.department_id)}
      emptyState={
        <EmptyState
          icon={<Building2 size={24} />}
          title="No department data"
          description="Department groupings are not available yet."
        />
      }
    />
  );
}
